import { useResizeObserver } from "@vueuse/core";
import { computed, ref } from "vue";

/**
 * A composable to get the current breakpoint state of a container.
 * The state is determined by the width of the container and the breakpoints passed to the composable.
 *
 * @param {HTMLElement|Object} target - The target element or Vue ref to observe.
 * @param {Object} [breakpoints={}] - An object containing the breakpoints to use.
 * @param {number} breakpoints.xxsmall.min - The minimum width for the xxsmall breakpoint.
 * @param {number} breakpoints.xxsmall.max - The maximum width for the xxsmall breakpoint.
 * @param {number} breakpoints.xsmall.min - The minimum width for the xsmall breakpoint.
 * @param {number} breakpoints.xsmall.max - The maximum width for the xsmall breakpoint.
 * @param {number} breakpoints.small.min - The minimum width for the small breakpoint.
 * @param {number} breakpoints.small.max - The maximum width for the small breakpoint.
 * @param {number} breakpoints.medium.min - The minimum width for the medium breakpoint.
 * @param {number} breakpoints.medium.max - The maximum width for the medium breakpoint.
 * @param {number} breakpoints.large.min - The minimum width for the large breakpoint.
 * @param {number} breakpoints.large.max - The maximum width for the large breakpoint.
 * @param {number} breakpoints.xlarge.min - The minimum width for the xlarge breakpoint.
 * @param {number} breakpoints.xlarge.max - The maximum width for the xlarge breakpoint.
 *
 * Each breakpoint is an object with `min` and `max` properties that define the range of widths for that breakpoint.
 * The `min` and `max` properties are optional. If only one of them is provided, the other will be set to `undefined`.
 * If both are provided, the width must be within that range to be considered in that breakpoint state.
 *
 * None of the breakpoints are required, and if none are provided, the function will return an object with all states set to `false`.
 * They can be provided in any order and can be omitted if not needed.
 *
 * @returns {Object} An object containing the current breakpoint state and modifier classes.
 *
 * @example
 * const breakpoints = {
 *   small: { min: 0, max: 600 },
 *   medium: { min: 601, max: 1200 },
 *   large: { min: 1201, max: Infinity },
 * };
 * const { isSmall, isMedium, isLarge } = useContainerBreakpoints(target, breakpoints);
 *
 * @example
 * const breakpoints = {
 *   small: { min: 0 }
 * };
 * const { isSmall } = useContainerBreakpoints(target, breakpoints);
 */
export function useContainerBreakpoints(target, breakpoints = {}) {
  const width = ref(null);

  useResizeObserver(target, ([entry]) => {
    width.value = Math.round(entry.contentRect.width);
  });

  const state = {
    isXXSmall: computed(() => evaluateBreakpointCondition(width.value, breakpoints.xxsmall)),
    isXSmall: computed(() => evaluateBreakpointCondition(width.value, breakpoints.xsmall)),
    isSmall: computed(() => evaluateBreakpointCondition(width.value, breakpoints.small)),
    isMedium: computed(() => evaluateBreakpointCondition(width.value, breakpoints.medium)),
    isLarge: computed(() => evaluateBreakpointCondition(width.value, breakpoints.large)),
    isXLarge: computed(() => evaluateBreakpointCondition(width.value, breakpoints.xlarge)),
  };

  const containerModifiers = computed(() =>
    [
      state.isXXSmall.value && "--is-xx-small-container",
      state.isXSmall.value && "--is-x-small-container",
      state.isSmall.value && "--is-small-container",
      state.isMedium.value && "--is-medium-container",
      state.isLarge.value && "--is-large-container",
      state.isXLarge.value && "--is-x-large-container",
    ].filter(Boolean),
  );

  return {
    ...state,
    containerModifiers,
  };
}

function evaluateBreakpointCondition(width, conditions) {
  if (width === null || !conditions) {
    return false;
  }

  const hasMin = conditions.min !== undefined;
  const hasMax = conditions.max !== undefined;

  if (!hasMin && !hasMax) {
    return false;
  }

  return (!hasMin || width >= conditions.min) && (!hasMax || width <= conditions.max);
}
